/**
 * The backend refuses to create a session that lands on the exact same class +
 * start minute as an existing one, answering 409 with:
 *
 *   { error: 'DUPLICATE_SESSION', message, duplicates: [{ classId, date, className }] }
 *
 * unless the request carried `allowDuplicate: true`. Every create dialog catches
 * this and shows a confirm prompt where the operator can uncheck the collisions
 * they don't want, then re-sends: the array paths drop the unchecked rows
 * client-side, the range path forwards them as `skip: [{ class, date }]`.
 */
export interface DuplicateEntry {
  classId: string;
  date: string;
  className: string;
  /** Multi-location create: which location already has it. */
  location?: string;
  studioId?: string;
}

export interface DuplicateSessionInfo {
  message: string;
  duplicates: DuplicateEntry[];
}

export function getDuplicateSessionError(err: any): DuplicateSessionInfo | null {
  // apiService's axios interceptor flattens errors to { status, data, message }
  // (no `.response`), but a few call sites still hand us a raw axios error —
  // accept either shape.
  const data = err?.data ?? err?.response?.data;
  const status = err?.status ?? err?.response?.status;
  if (status === 409 && data && data.error === 'DUPLICATE_SESSION') {
    return {
      message:
        typeof data.message === 'string' && data.message
          ? data.message
          : 'A session already exists at this date and time.',
      duplicates: Array.isArray(data.duplicates) ? data.duplicates : [],
    };
  }
  return null;
}

/**
 * Stable identity for a session slot — class id + the minute it starts on.
 * Seconds/millis are floored so a payload row and the existing session the
 * backend matched it against resolve to the same key.
 */
export function dupKey(classId: string, date: string | Date): string {
  const ms = date instanceof Date ? date.getTime() : new Date(date).getTime();
  return `${classId}@${Number.isNaN(ms) ? String(date) : Math.floor(ms / 60000)}`;
}

/**
 * Removes from a pending create payload the rows the operator unchecked in the
 * duplicate prompt. `rows` are the payload entries (each with `class` + `date`);
 * `skip` is the set of duplicates they chose NOT to create.
 */
export function dropSkipped<T extends { class: string; date: string | Date }>(
  rows: T[],
  skip: DuplicateEntry[],
): T[] {
  if (!skip.length) return rows;
  const skipKeys = new Set(skip.map((d) => dupKey(d.classId, d.date)));
  return rows.filter((r) => !skipKeys.has(dupKey(r.class, r.date)));
}
